import React from "react";

import { OnboardingStep } from "graphql/types";
import { useAppRouter } from "lib/useAppRouter";
import { Route } from "lib/routes";
import links from "lib/links";
import { useFinancialInstitutions } from "components/integrations/shared/FinancialInstitutionsProvider";
import { LaunchpadTask } from "../analytics";
import { LaunchpadStep } from "../launchpadStore";
import { TaskTile } from "../TaskTile";
import { LaunchpadTaskSetProps } from "./LaunchpadTaskSetProps";
import { useActiveCompany } from "components/companies/ActiveCompanyProvider";

export const BookkeepersTaskSet = ({
  fullSize,
  localProgress,
  onboardingProgress,
  onComplete,
  onTaskClick,
  onGoToStepAndShow,
}: LaunchpadTaskSetProps) => {
  const { goToPath } = useAppRouter();
  const { accounts, accountsLoading } = useFinancialInstitutions();
  const { company } = useActiveCompany();

  return (
    <>
      <TaskTile
        heading={LaunchpadTask.CustomizeChartOfAccounts}
        subheading="Add, remove, or edit accounts to suit your needs."
        analyticsTask={LaunchpadTask.CustomizeChartOfAccounts}
        disabled={false}
        fullSize={fullSize}
        stepStatus={
          localProgress?.customizeChartOfAccounts ??
          onboardingProgress?.companyOnboardingProgress?.customizeChartOfAccounts
        }
        onClick={() =>
          onTaskClick(
            () => goToPath(Route.chartOfAccountsSettings),
            localProgress?.customizeChartOfAccounts ??
              onboardingProgress?.companyOnboardingProgress?.customizeChartOfAccounts
          )
        }
        onComplete={() =>
          onComplete(
            OnboardingStep.CustomizeChartOfAccounts,
            localProgress?.customizeChartOfAccounts ??
              onboardingProgress?.companyOnboardingProgress?.customizeChartOfAccounts
          )
        }
      />
      <TaskTile
        heading={LaunchpadTask.ConfigureClassesAndDepartments}
        subheading="Create classes or departments to track spend by team, project or location."
        analyticsTask={LaunchpadTask.ConfigureClassesAndDepartments}
        disabled={false}
        fullSize={fullSize}
        stepStatus={
          localProgress?.configureClassesAndDepartments ??
          onboardingProgress?.userOnboardingProgress?.configureClassesAndDepartments
        }
        onClick={() =>
          onTaskClick(
            () => goToPath(Route.classesSettings),
            localProgress?.configureClassesAndDepartments ??
              onboardingProgress?.userOnboardingProgress?.configureClassesAndDepartments
          )
        }
        onComplete={() =>
          onComplete(
            OnboardingStep.ConfigureClassesAndDepartments,
            localProgress?.configureClassesAndDepartments ??
              onboardingProgress?.userOnboardingProgress?.configureClassesAndDepartments
          )
        }
      />
      <TaskTile
        heading={LaunchpadTask.ConfigureAutomationSettings}
        subheading="Toggle subledgers, auto-balance adjustments, enhanced auto-categorization and more to help you work more efficiently and accurately"
        analyticsTask={LaunchpadTask.ConfigureAutomationSettings}
        disabled={false}
        fullSize={fullSize}
        stepStatus={
          localProgress?.configureAutomationSettings ??
          onboardingProgress?.companyOnboardingProgress?.configureAutomationSettings
        }
        onClick={() =>
          onTaskClick(
            () => goToPath(Route.automationSettings),
            localProgress?.configureAutomationSettings ??
              onboardingProgress?.companyOnboardingProgress?.configureAutomationSettings
          )
        }
        onComplete={() =>
          onComplete(
            OnboardingStep.ConfigureAutomationSettings,
            localProgress?.configureAutomationSettings ??
              onboardingProgress?.companyOnboardingProgress?.configureAutomationSettings
          )
        }
      />
      <TaskTile
        heading={LaunchpadTask.CreateCustomRules}
        subheading="Create rules to automatically categorize transactions based on specific criteria (e.g. amount, vendor, etc.)."
        analyticsTask={LaunchpadTask.CreateCustomRules}
        disabled={false}
        fullSize={fullSize}
        stepStatus={
          localProgress?.createCustomRules ??
          onboardingProgress?.companyOnboardingProgress?.createCustomRules
        }
        onClick={() =>
          onTaskClick(
            () => goToPath(Route.rulesSettings),
            localProgress?.createCustomRules ??
              onboardingProgress?.companyOnboardingProgress?.createCustomRules
          )
        }
        onComplete={() =>
          onComplete(
            OnboardingStep.CreateCustomRules,
            localProgress?.createCustomRules ??
              onboardingProgress?.companyOnboardingProgress?.createCustomRules
          )
        }
      />
      <TaskTile
        heading={LaunchpadTask.CreateRevenueRecognitionPolicies}
        subheading="Control how you want to recognize revenue across a range of use cases to maintain accurate accrual-basis financial statements."
        analyticsTask={LaunchpadTask.CreateRevenueRecognitionPolicies}
        disabled={false}
        fullSize={fullSize}
        stepStatus={
          localProgress?.createRevenueRecognitionPolicies ??
          onboardingProgress?.companyOnboardingProgress?.createRevenueRecognitionPolicies
        }
        onClick={() =>
          onTaskClick(
            () => goToPath(Route.policySettings),
            localProgress?.createRevenueRecognitionPolicies ??
              onboardingProgress?.companyOnboardingProgress?.createRevenueRecognitionPolicies
          )
        }
        onComplete={() =>
          onComplete(
            OnboardingStep.CreateRevenueRecognitionPolicies,
            localProgress?.createRevenueRecognitionPolicies ??
              onboardingProgress?.companyOnboardingProgress?.createRevenueRecognitionPolicies
          )
        }
      />
      <TaskTile
        heading={LaunchpadTask.InviteClientsToConnectFinancialAccounts}
        subheading="Send an invite so your client can securely connect bank, card, payroll or Stripe."
        analyticsTask={LaunchpadTask.InviteClientsToConnectFinancialAccounts}
        disabled={false}
        fullSize={fullSize}
        stepStatus={
          localProgress?.inviteClientsToConnectFinancialAccounts ??
          onboardingProgress?.userOnboardingProgress?.inviteClientsToConnectFinancialAccounts
        }
        onClick={() =>
          onTaskClick(
            () => goToPath(Route.usersSettings),
            localProgress?.inviteClientsToConnectFinancialAccounts ??
              onboardingProgress?.userOnboardingProgress?.inviteClientsToConnectFinancialAccounts
          )
        }
        onComplete={() =>
          onComplete(
            OnboardingStep.InviteClientsToConnectFinancialAccounts,
            localProgress?.inviteClientsToConnectFinancialAccounts ??
              onboardingProgress?.userOnboardingProgress?.inviteClientsToConnectFinancialAccounts
          )
        }
      />
      {!accountsLoading && accounts.length > 0 && (
        <TaskTile
          heading={LaunchpadTask.ReviewTransactions}
          subheading="Review transactions for accuracy and quickly edit any that need updates."
          analyticsTask={LaunchpadTask.ReviewTransactions}
          disabled={false}
          fullSize={fullSize}
          stepStatus={
            localProgress?.reviewTransactions ??
            onboardingProgress?.userOnboardingProgress?.reviewTransactions
          }
          onClick={() =>
            onTaskClick(
              () => goToPath(Route.transactions),
              localProgress?.reviewTransactions ??
                onboardingProgress?.userOnboardingProgress?.reviewTransactions
            )
          }
          onComplete={() =>
            onComplete(
              OnboardingStep.ReviewTransactions,
              localProgress?.reviewTransactions ??
                onboardingProgress?.userOnboardingProgress?.reviewTransactions
            )
          }
        />
      )}
      <TaskTile
        heading={LaunchpadTask.InviteFirmsTeamMembers}
        subheading="Loop in colleagues for review or reconciliation tasks."
        analyticsTask={LaunchpadTask.InviteFirmsTeamMembers}
        disabled={false}
        fullSize={fullSize}
        stepStatus={
          localProgress?.inviteFirmsTeamMembers ??
          onboardingProgress?.userOnboardingProgress?.inviteFirmsTeamMembers
        }
        onClick={() =>
          onTaskClick(
            () => goToPath(Route.usersSettings),
            localProgress?.inviteFirmsTeamMembers ??
              onboardingProgress?.userOnboardingProgress?.inviteFirmsTeamMembers
          )
        }
        onComplete={() =>
          onComplete(
            OnboardingStep.InviteFirmsTeamMembers,
            localProgress?.inviteFirmsTeamMembers ??
              onboardingProgress?.userOnboardingProgress?.inviteFirmsTeamMembers
          )
        }
      />
      {company?.hasHistoricalData && (
        <TaskTile
          heading={LaunchpadTask.StartHistoricalTransferProcess}
          subheading="Upload or schedule migration of historical data so Puzzle can give you full context."
          analyticsTask={LaunchpadTask.StartHistoricalTransferProcess}
          disabled={false}
          fullSize={fullSize}
          completeOnClick={false}
          stepStatus={
            localProgress?.startHistoricalTransferProcess ??
            onboardingProgress?.companyOnboardingProgress?.startHistoricalTransferProcess
          }
          onClick={() =>
            onGoToStepAndShow(
              LaunchpadStep.StartHistoricalTransferProcess,
              localProgress?.startHistoricalTransferProcess ??
                onboardingProgress?.companyOnboardingProgress?.startHistoricalTransferProcess
            )
          }
          onComplete={() =>
            onComplete(
              OnboardingStep.StartHistoricalTransferProcess,
              localProgress?.startHistoricalTransferProcess ??
                onboardingProgress?.companyOnboardingProgress?.startHistoricalTransferProcess
            )
          }
        />
      )}
      <TaskTile
        heading={LaunchpadTask.GetPuzzleCertified}
        subheading="Take the 30-min certification to unlock priority support & partner perks."
        analyticsTask={LaunchpadTask.GetPuzzleCertified}
        disabled={false}
        fullSize={fullSize}
        stepStatus={
          localProgress?.getPuzzleCertified ??
          onboardingProgress?.userOnboardingProgress?.getPuzzleCertified
        }
        onClick={() =>
          onTaskClick(
            () => window.open(links.puzzleCertification, "_blank")?.focus(),
            localProgress?.getPuzzleCertified ??
              onboardingProgress?.userOnboardingProgress?.getPuzzleCertified
          )
        }
        onComplete={() =>
          onComplete(
            OnboardingStep.GetPuzzleCertified,
            localProgress?.getPuzzleCertified ??
              onboardingProgress?.userOnboardingProgress?.getPuzzleCertified
          )
        }
      />
    </>
  );
};
